import type { KeyboardEvent } from 'react';
import {
  useContext, useEffect, useRef, useState,
} from 'react';
import {
  SidebarMenuItem,
  Popover,
  Button,
  IconName,
  Icon,
  Tooltip,
  Separator,
  ButtonVariant,
  Text,
  TextField,
  useSnackBar,
} from '@usealloy/component-library';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { UserContext } from '~/dashboard/context';
import type { UserContextValue } from '~/dashboard/context';
import { routes } from '~/helpers/routes';
import log from '~/helpers/logging';
import { useGetRequestApprovers } from '~/dashboard/admin/provisioning/helpers/useGetRequestApprovers';
import { useCreateAccessRequestMutation } from '~/dashboard/admin/provisioning/helpers/useCreateAccessRequestMutation';
import RequestAccessModal from '~/dashboard/views/customers/RequestAccessModal';
import { overflowShadow, overflowShadowTransition } from '../components/OverlayScrollbar';
import { hoverRevealCaretStyles } from './FlyoutTriggerCaret';
import { RbacCountdown } from './RbacCountdown';
import { AllCustomersSection } from './CustomerSwitcher/AllCustomersSection';
import type { AllCustomersSectionHandle } from './CustomerSwitcher/AllCustomersSection';
import { ALL_CUSTOMERS_POPOVER_MIN_WIDTH, HIGHLIGHT_BACKGROUND } from './CustomerSwitcher/constants';
import type { AllCustomersRow } from './CustomerSwitcher/types';

// Types

interface CustomerSwitcherProps {
  expanded?: boolean;
}

interface StyledSidebarMenuItemProps {
  warn?: boolean;
}

// Utils

const getInitials = (name: string) => name
  .split(/\s+/)
  .map(word => word.replaceAll(/[^a-z]/gi, '').charAt(0))
  .filter(Boolean)
  .slice(0, 4)
  .join('')
  .toUpperCase();

const getOrgsToDisplay = ({
  customer,
  customerAgents,
}: {
  customer: UserContextValue['customer'];
  customerAgents?: Array<{
    customerId?: number;
    customerAgentId: number;
    organizationName: string;
    organizationNameSlug: string;
    primaryAccount: boolean;
    customerAgentActive: boolean;
    archived?: boolean;
  }>;
}) => {
  const currentOrg = [];
  const otherAvailableOrgItems = [];

  for (const ca of customerAgents ?? []) {
    const org = {
      orgId: ca.customerId,
      orgName: ca.organizationName,
      orgNameSlug: ca.organizationNameSlug,
      customerAgentId: ca.customerAgentId,
      primaryAccount: ca.primaryAccount,
    };

    if (ca.customerId === customer.customerId) {
      currentOrg.push(org);
    } else if (ca.customerAgentActive && !ca.archived) {
      otherAvailableOrgItems.push(org);
    }
  }

  return { currentOrg: currentOrg.at(0), otherAvailableOrgItems };
};

// Internal components

const IconContainer = styled('span')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  height: 24,
  width: 24,

  backgroundColor: '#191919', // theme.palette.background.accent in FE-P
  color: '#FAFAF9', // theme.palette.text.accent in FE-P
  borderRadius: '4px', // theme.borderRadius['1'] in FEP-P

  fontSize: '0.75rem', // theme.typography.mixins.body({size: "medium"})
  lineHeight: '1.125rem', // theme.typography.mixins.body({size: "medium"})
  fontWeight: '700', // theme.typography.fontWeight.bold

  '&[data-expanded="false"]': {
    width: '100%',
    transitionDelay: '100ms', // theme.animation.duration.shortest
  },

  transitionProperty: 'width padding-inline',
  transitionDuration: '300ms', // theme.animation.duration.standard
  transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)', // theme.animation.motion["ease-in-out"]

  svg: {
    color: '#FAFAF9',
    height: 16,
    width: 16,
  },
});

const OrgNameText = styled(Text)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  flex: 1,
});

const StyledSidebarMenuItem = styled(
  SidebarMenuItem,
)<StyledSidebarMenuItemProps>`
  width: 100%;
  --bg-color: ${props => (props.warn ? '#FBA6A7' : '#EBEBE6')};
  --bg-color-hover: ${props => (props.warn
    ? 'oklch(from #FBA6A7 calc(l * .9) c h)'
    : 'oklch(from #EBEBE6 calc(l * .9) c h)')};
  border-radius: 0;
  display: flex;
  flex-direction: row;
  height: 48px;

  &[data-expanded="false"] {
    padding-inline: 12px;
  }

  &[data-expanded="true"] {
    padding-inline: 28px;
  }

  ${hoverRevealCaretStyles}
`;

const SwitchableAgentButton = styled(Button)({
  width: '100%',
  justifyContent: 'flex-start',
  background: 'none',
  border: 'none',
  boxShadow: 'none',
  padding: '6px 12px',
  fontWeight: 400,
  borderRadius: 0,
  '&:hover': {
    background: 'oklch(from #EBEBE6 calc(l * .95) c h)',
  },
});

const SectionLabel = styled(Text)({
  padding: '8px 12px 4px',
  color: '#646461',
});

const CurrentOrgItem = styled(Text)({
  width: '100%',
  padding: '8px 12px',
});

/**
 * The pinned "Customer List" action at the bottom of the popover, playing the
 * same role `Sidebar.Footer` does: it sits below the scrolling All Customers
 * list and casts the overflow shadow up over it whenever there is something to
 * scroll, in place of a hairline separator.
 */
const CustomerListFooter = styled.div({
  padding: '8px',

  ...overflowShadowTransition,

  '[data-component="CustomerSwitcher.AllCustomers"][data-overflowing="true"] + &': {
    boxShadow: overflowShadow,
  },
});

// CustomerSwitcher

export const CustomerSwitcher = ({ expanded }: CustomerSwitcherProps) => {
  const {
    agent, customer, adminCustomerAccess, disableCustomerPicker, alloyEnvironment,
  } = useContext(UserContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showRequestAccessModal, setShowRequestAccessModal] = useState(false);
  const [requestAccessCustomerId, setRequestAccessCustomerId] = useState<number>();
  const addSnackBar = useSnackBar();

  // Keyboard navigation (FRONTEND-664 follow-up) — a single index spanning
  // "My Accounts" -> "All Customers" -> "Customer List", so arrow keys move
  // seamlessly across all three, matching the FE-P switcher's cmdk-driven
  // behavior. "All Customers" is virtualized, so its own row count is reported
  // up via onCountChange rather than computed here, and activation goes
  // through a ref (activateCustomerRow works off row data, not a mounted
  // component, since react-virtualized only mounts visible rows).
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [allCustomersCount, setAllCustomersCount] = useState(0);
  const allCustomersSectionRef = useRef<AllCustomersSectionHandle>(null);

  const location = useLocation();
  const navigate = useNavigate();

  // Admin-only "All Customers" search section (FRONTEND-664) — lazily fetched
  // only once the popover is actually open.
  const shouldShowAllCustomers = Boolean(agent?.isEmployee) && !disableCustomerPicker;
  const currentCellKey = (alloyEnvironment ?? '').replace('kube', '');

  // /v3/dashboard/admin/customers redirects anyone who isn't an employee or
  // AFEF parent agent back to /settings (see dashboard/index.tsx's route
  // definition for that path) — mirror the same gate here so agents without
  // access to the page don't see a dead-end "Customer List" button.
  const canSeeCustomerList = Boolean(agent?.isEmployee || agent?.isAfefParentAgent);

  // Gated on `shouldShowAllCustomers` (employees only), which is also the gate on
  // rendering the section that opens the request-access modal — so an agent who is
  // both an employee and an AFEF parent still gets approvers to pick from, instead
  // of an empty, unsubmittable form.
  const { data: requestApprovers = [] } = useGetRequestApprovers({
    enabled: isPopoverOpen && shouldShowAllCustomers,
    onError: error => {
      log('error', 'Error fetching request approvers for sidebar customer switcher', {}, error);
      addSnackBar({ title: 'Unexpected error loading request approvers', type: 'danger' });
    },
  });

  // `mutate`, not `mutateAsync`: the modal's `onSubmit` discards what
  // `createAccessRequest` returns, so a rejected `mutateAsync` promise would go
  // unhandled. Failures are already surfaced by `onError` below.
  const { mutate: createAccessRequest } = useCreateAccessRequestMutation({
    onError: () => {
      addSnackBar({ title: 'There was an issue requesting access', type: 'danger' });
    },
    onSuccess: result => {
      if (result?.notify_success) {
        addSnackBar({
          title: 'Your temporary access request has been submitted successfully',
          type: 'success',
        });
      }
    },
  });

  const handleRequestAccess = (row: AllCustomersRow) => {
    setIsPopoverOpen(false);
    setRequestAccessCustomerId(row.customer_id);
    setShowRequestAccessModal(true);
  };

  const inAdminDashboard = location.pathname.startsWith('/v3/dashboard/admin');
  const shouldWarn = agent?.isEmployee
    && customer?.parentOrganizationSlug?.toLowerCase() !== 'alloy';

  const isInCustomerAccount = customer?.customerAgentId === null;

  // `adminCustomerAccess` is keyed by `organization_name_slug` in the raw API,
  // but `convertToCamel` (helpers/api.js) recursively camelCases *every* object
  // key on the way in — so a slug key like `acme-bank` becomes `acmeBank` while
  // the customer's `organizationNameSlug` *value* stays `acme-bank`. A direct
  // `adminCustomerAccess[organizationNameSlug]` lookup therefore misses for any
  // multi-word slug, which is why the counter never rendered. Look the record up
  // by its preserved `organizationNameSlug` value instead — matching how
  // index.tsx and CustomerAccessBanner already read this data.
  const rbacAccess = customer?.organizationNameSlug
    ? Object.values(adminCustomerAccess ?? {}).find(
      access => access.organizationNameSlug === customer?.organizationNameSlug,
    )
    : undefined;
  const rbacExpirationTimestamp = rbacAccess?.expiredAt
    ? new Date(rbacAccess.expiredAt).getTime()
    : 0;
  const shouldShowExpirationTimestamp = isInCustomerAccount && rbacExpirationTimestamp !== 0;

  const getOrganizationName = () => {
    if (inAdminDashboard) {
      return agent?.isEmployee
        ? 'Alloy Internal Dashboard'
        : customer?.organizationName
            || agent?.customerAgents?.[0]?.organizationName;
    }
    return customer?.organizationName;
  };

  const { currentOrg, otherAvailableOrgItems } = getOrgsToDisplay({
    customer: customer ?? {},
    customerAgents: agent?.customerAgents,
  });

  const orgName = getOrganizationName() || '';
  const orgInitials = getInitials(customer?.organizationName ?? '');

  const CustomerSwitcherLabel = (
    <>
      <Tooltip disabled={expanded} content={orgName} side="right">
        <IconContainer data-expanded={expanded}>
          {expanded ? <Icon icon={IconName.Alloy} /> : orgInitials}
        </IconContainer>
      </Tooltip>
      <OrgNameText id="current-organization-name">
        {customer?.organizationName}
      </OrgNameText>

      {!!shouldShowExpirationTimestamp && (
        <RbacCountdown expiredAt={rbacExpirationTimestamp} />
      )}
      {!disableCustomerPicker && <Icon icon={IconName.CaretRight} />}
    </>
  );

  const filteredOrgItems = otherAvailableOrgItems.filter(org => (searchQuery
    ? org.orgName.toLowerCase().includes(searchQuery.toLowerCase())
    : true),
  );

  const myAccountsCount = filteredOrgItems.length;
  const effectiveAllCustomersCount = shouldShowAllCustomers ? allCustomersCount : 0;
  const totalKeyboardItems = myAccountsCount
    + effectiveAllCustomersCount
    + (canSeeCustomerList ? 1 : 0);

  // Reset the highlight whenever the popover (re)opens or the search
  // narrows/widens the list, so it doesn't point at a stale position.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [isPopoverOpen, searchQuery]);

  // Clamp if the list shrinks (e.g. "All Customers" finishes loading with
  // fewer rows than the placeholder count) so the highlight never points past
  // the end of the list.
  useEffect(() => {
    setHighlightedIndex(current => {
      if (totalKeyboardItems === 0) return 0;
      return Math.min(current, totalKeyboardItems - 1);
    });
  }, [totalKeyboardItems]);

  // A full page load, not a client-side navigate: /switch_customer is a Django
  // view that swaps the session's customer before redirecting back into the
  // dashboard. Shared by click and keyboard activation so both go through the
  // same route.
  const switchToOrg = (orgId?: number) => {
    window.location.href = routes.switchCustomer(orgId);
  };

  const activateHighlightedItem = () => {
    if (highlightedIndex < myAccountsCount) {
      const org = filteredOrgItems[highlightedIndex];
      if (org) switchToOrg(org.orgId);
      return;
    }

    const allCustomersIndex = highlightedIndex - myAccountsCount;
    if (shouldShowAllCustomers && allCustomersIndex < effectiveAllCustomersCount) {
      allCustomersSectionRef.current?.activateRow(allCustomersIndex);
      return;
    }

    if (canSeeCustomerList) {
      navigate(routes.v3.dashboard.admin.customers.list);
    }
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (totalKeyboardItems === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(current => Math.min(current + 1, totalKeyboardItems - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(current => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      activateHighlightedItem();
    }
    // Escape is already handled by the Popover's own dismissable-layer.
  };

  return (
    <>
      {!disableCustomerPicker ? (
        <Popover
          isOpen={isPopoverOpen}
          onOpenChange={setIsPopoverOpen}
          trigger={(
            <StyledSidebarMenuItem warn={shouldWarn} data-expanded={expanded}>
              {CustomerSwitcherLabel}
            </StyledSidebarMenuItem>
          )}
          side="right"
          align="start"
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            minWidth: `${ALL_CUSTOMERS_POPOVER_MIN_WIDTH}px`,
          }}
        >
          <div style={{ padding: '8px 8px 4px' }}>
            <TextField
              type="text"
              leadingIcon={IconName.Search}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search..."
              value={searchQuery}
              style={{ width: '100%' }}
            />
          </div>

          {!!currentOrg && searchQuery.length === 0 && (
            <>
              <SectionLabel size={1}>Current</SectionLabel>
              <CurrentOrgItem size={2}>{currentOrg?.orgName}</CurrentOrgItem>
            </>
          )}

          {filteredOrgItems.length > 0 && (
            <>
              <SectionLabel size={1}>My Accounts</SectionLabel>
              {filteredOrgItems.map((org, index) => (
                <SwitchableAgentButton
                  key={`${org.orgId}-${org.customerAgentId}`}
                  onClick={() => switchToOrg(org.orgId)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  style={highlightedIndex === index
                    ? { background: HIGHLIGHT_BACKGROUND }
                    : undefined}
                  variant={ButtonVariant.tertiary}
                >
                  {`${org.orgName}${org.primaryAccount ? ' (primary)' : ''}`}
                </SwitchableAgentButton>
              ))}
            </>
          )}

          {shouldShowAllCustomers && <Separator />}
          {shouldShowAllCustomers && (
            <AllCustomersSection
              ref={allCustomersSectionRef}
              enabled={isPopoverOpen}
              currentCellKey={currentCellKey}
              onRequestAccess={handleRequestAccess}
              searchQuery={searchQuery}
              highlightedIndex={highlightedIndex - myAccountsCount}
              onCountChange={setAllCustomersCount}
              onRowHover={index => setHighlightedIndex(myAccountsCount + index)}
            />
          )}

          {canSeeCustomerList && (
            <>
              {/*
                * When All Customers is present it scrolls right above this
                * footer, so the boundary is drawn by the footer's overflow
                * shadow instead. Agents without that section (AFEF parents)
                * have nothing scrolling here, so they keep the hairline.
                */}
              {!shouldShowAllCustomers && <Separator />}
              <CustomerListFooter data-component="CustomerSwitcher.Footer">
                <Button
                  icon={IconName.ArrowLeft}
                  onClick={() => {
                    navigate(routes.v3.dashboard.admin.customers.list);
                  }}
                  onMouseEnter={() => setHighlightedIndex(totalKeyboardItems - 1)}
                  style={{
                    width: '100%',
                    // Button defaults to justify-content: center; FE-P's
                    // equivalent link is explicitly left-aligned — match it.
                    justifyContent: 'flex-start',
                    ...(highlightedIndex === totalKeyboardItems - 1
                      && { background: HIGHLIGHT_BACKGROUND }),
                  }}
                  variant={ButtonVariant.tertiary}
                >
                  Customer List
                </Button>
              </CustomerListFooter>
            </>
          )}
        </Popover>
      ) : (
        <StyledSidebarMenuItem warn={shouldWarn}>
          {CustomerSwitcherLabel}
        </StyledSidebarMenuItem>
      )}

      {showRequestAccessModal && (
        <RequestAccessModal
          isOpen
          approverOptions={requestApprovers}
          onToggleModal={() => setShowRequestAccessModal(false)}
          createAccessRequest={formData => createAccessRequest({
            requestAccessCustomerId, formData,
          })}
        />
      )}
    </>
  );
};
